/**
 * NewLogin saga
 */

import { all, put, call, takeLatest } from 'redux-saga/effects';
import * as Api from '../../../services/api';
import * as constants from './constants';
import { setAuthenticationDetails } from '../../../utils/authWrapper';
import { saveItem } from '../../../services/localStorageApi';
import { moduleRedirector } from '../Cap/utils';
import { getConfigValue } from 'utils/commonUtils';
import { encryptMD5 } from '../../../utils/authUtils';
import history from '../../../utils/history';

export function* loginV2Saga({ body }) {
  try {
    // Encrypt the password before sending to API
    const encryptedPayload = {
      ...body,
      password: encryptMD5(body.password),
      isPreHashed: true,
    };
    saveItem('loginUsername', body.username);

    const response = yield call(Api.loginV2, { body: encryptedPayload });
    
    if (response?.success) {
      yield put({
        type: constants.LOGIN_V2_SUCCESS,
      });

      if (response?.result?.requiresOTP) {
        const otpUrl = getConfigValue('otp_url');
        history.push(otpUrl);
      } else {
        setAuthenticationDetails(response.result);
        moduleRedirector(response.result.user?.preferredModuleUrl);
      }

    } else {
      // Error cases (success: false) - check status code for specific messages
      const statusCode = response?.status;
      const apiMessage = response?.message || '';
      let errorMessage;
      
      if (statusCode === 423) {
        const accountLockedUrl = getConfigValue('account_locked_url');
        history.push(accountLockedUrl.replace(':type', 'login'));
        yield put({
          type: constants.LOGIN_V2_FAILURE,
          error: 'Account is locked temporarily.',
        });
        return;
      } else if (statusCode === 401) {
        // Arya sends 401 both for genuine wrong-password and for an SSO-enabled org
        // rejecting the credential login (Zion LoginDisallowed) — surface its real
        // message when present (e.g. the SSO case), else the generic credentials copy.
        errorMessage = apiMessage || constants.INVALID_CREDENTIALS_MESSAGE;
      } else if (statusCode === 500) {
        errorMessage = 'There is an issue with the system. Try again in some time';
      } else {
        errorMessage = apiMessage || 'Login failed. Please check your credentials.';
      }
      
      yield put({
        type: constants.LOGIN_V2_FAILURE,
        error: errorMessage,
      });
    }
    
  } catch (error) {
    yield put({
      type: constants.LOGIN_V2_FAILURE,
      error: error.message || 'Network error. Please try again.',
    });
  }
}

export function* ssoLoginV2Saga() {
  try {
    const response = yield call(Api.ssoLoginV2);
    
    if (response?.success && response?.result?.oauthUrl) {
      // Redirect to the OAuth URL returned by the API
      window.location.href = response.result.oauthUrl;
    } else {
      console.error('SSO login failed - no OAuth URL received');
    }
    
  } catch (error) {
    console.error('SSO login failed:', error.message);
  }
}

export function* checkSsoDomainSaga({ email }) {
  try {
    const response = yield call(Api.checkSsoDomain, { body: { email } });
    
    // 404 - SSO not available, show password field
    if (response?.status === 404 || (response?.success === false && response?.requiresPassword)) {
      console.log('[checkSsoDomainSaga] No SSO for domain, showing password field');
      
      yield put({
        type: constants.SHOW_PASSWORD_FIELD,
      });
      return;
    }
    
    // 429 - Rate limited
    if (response?.status === 429) {
      console.warn('[checkSsoDomainSaga] Rate limited');
      
      yield put({
        type: constants.CHECK_SSO_DOMAIN_FAILURE,
        error: response?.message || response?.error || 'Too many attempts. Please try again later.',
      });
      return;
    }
    
    // 400 - Invalid email
    if (response?.status === 400) {
      yield put({
        type: constants.CHECK_SSO_DOMAIN_FAILURE,
        error: response?.message || response?.error || 'Invalid email format',
      });
      return;
    }
    
    // Other errors or unexpected responses
    if (!response?.success) {
      yield put({
        type: constants.CHECK_SSO_DOMAIN_FAILURE,
        error: response?.message || response?.error || 'Failed to check SSO availability',
      });
      return;
    }

    // If backend explicitly marks this email for password auth, show password flow.
    const normalizedEmail = (email || '').trim().toLowerCase();
    const userPasswordConfig = response?.result?.userPasswordConfig;
    const shouldShowPasswordField = Array.isArray(userPasswordConfig)
      && userPasswordConfig.some((configuredEmail) => (
        typeof configuredEmail === 'string'
        && configuredEmail.trim().toLowerCase() === normalizedEmail
      ));

    if (shouldShowPasswordField) {
      yield put({
        type: constants.SHOW_PASSWORD_FIELD,
      });
      return;
    }
    

    // SSO available: backend returned oauthUrl (checkSsoDomainV2 internally calls ssoLoginV2). Redirect in UI.
    if (response?.success && response?.result?.oauthUrl) {
      saveItem('isLoggedIn', true);
      window.location.href = response.result.oauthUrl;
      return;
    }

    // Fallback: success but no oauthUrl
    yield put({
      type: constants.CHECK_SSO_DOMAIN_SUCCESS,
    });
    
  } catch (error) {
    console.error('[checkSsoDomainSaga] Error:', error);
    
    // Check if it's a redirect error (302) - browser should handle it
    if (error.message && (error.message.includes('redirect') || error.message.includes('302'))) {
      // Browser is handling redirect, clear loading state
      yield put({
        type: constants.CHECK_SSO_DOMAIN_SUCCESS,
      });
      return;
    }
    
    // Always dispatch failure to clear loading state
    yield put({
      type: constants.CHECK_SSO_DOMAIN_FAILURE,
      error: error.message || error?.response?.message || 'Network error. Please try again.',
    });
  }
}


export function* validateOTPSaga({ body }) {
  try {
    const response = yield call(Api.validateOTP, { body });
    
    if (response?.success) {
      yield put({
        type: constants.VALIDATE_OTP_SUCCESS,
      });
      
      setAuthenticationDetails(response.result);
      moduleRedirector(response.result.user?.preferredModuleUrl);
    } else {
      // Error cases (success: false) - check status code for specific messages
      const statusCode = response?.status;
      const apiMessage = response?.message || '';
      let errorMessage;
      
      if (statusCode === 423) {
        const accountLockedUrl = getConfigValue('account_locked_url');
        history.push(accountLockedUrl.replace(':type', 'otp'));
        yield put({
          type: constants.VALIDATE_OTP_FAILURE,
          error: 'Account is locked temporarily.',
        });
        return;
      } else if (statusCode === 401) {
        errorMessage = 'The OTP entered is incorrect';
      } else if (statusCode === 500) {
        errorMessage = 'There is an issue with the system. Try again in some time';
      } else {
        errorMessage = apiMessage || 'OTP validation failed. Please try again.';
      }
      
      yield put({
        type: constants.VALIDATE_OTP_FAILURE,
        error: errorMessage,
      });
    }
    
  } catch (error) {
    yield put({
      type: constants.VALIDATE_OTP_FAILURE,
      error: error.message || 'Network error. Please try again.',
    });
  }
}

export function* resendOTPSaga({ onSuccess }) {
  try {
    const response = yield call(Api.resendOTP);
    
    if (response?.success) {
      yield put({
        type: constants.RESEND_OTP_SUCCESS,
      });
      
      if (onSuccess) {
        onSuccess();
      }
    } else {
      // Error cases (success: false) - check status code for specific messages
      const statusCode = response?.status;
      const apiMessage = response?.message || '';
      let errorMessage;
      
      if (statusCode === 423) {
        errorMessage = 'Too many OTP requests. Please wait before requesting again.';
      } else if (statusCode === 500) {
        errorMessage = 'There is an issue with the system. Try again in some time';
      } else {
        errorMessage = apiMessage || 'Failed to resend OTP. Please try again.';
      }
      
      yield put({
        type: constants.RESEND_OTP_FAILURE,
        error: errorMessage,
      });
    }
    
  } catch (error) {
    yield put({
      type: constants.RESEND_OTP_FAILURE,
      error: error.message || 'Network error. Please try again.',
    });
  }
}

export function* forgotPasswordSaga({ body }) {
  try {
    const response = yield call(Api.forgotPassword, { body });
    
    if (response?.success) {
      yield put({
        type: constants.FORGOT_PASSWORD_SUCCESS,
      });
    } else {
      yield put({
        type: constants.FORGOT_PASSWORD_FAILURE,
        error: 'An error occurred. Please try again after some time',
      });
    }
  } catch (error) {
    yield put({
      type: constants.FORGOT_PASSWORD_FAILURE,
      error: 'Network error. Please check your connection and try again.',
    });
  }
}

export function* setPasswordSaga({ body }) {
  try {
    const encryptedPayload = {
      new_password: encryptMD5(body.password),
      confirm_password: encryptMD5(body.confirmPassword),
      cookie: body.cookie,
      user_id: body.user_id,
      verification_code: body.verification_code,
    };

    const response = yield call(Api.setPassword, { body: encryptedPayload });
    
    if (response?.success) {
      yield put({
        type: constants.SET_PASSWORD_SUCCESS,
      });
    } else {
      yield put({
        type: constants.SET_PASSWORD_FAILURE,
        error: response.message,
      });
    }
    
  } catch (error) {
    yield put({
      type: constants.SET_PASSWORD_FAILURE,
      error: error.message || 'Network error. Please try again.',
    });
  }
}

export function* checkOtpSessionSaga() {
  try {
    const response = yield call(Api.getOtpSession);
    if (response?.success) {
      yield put({ type: constants.OTP_SESSION_CHECK_SUCCESS });
    } else {
      yield put({ type: constants.OTP_SESSION_CHECK_FAILURE });
    }
  } catch (error) {
    yield put({ type: constants.OTP_SESSION_CHECK_FAILURE });
  }
}

export function* logoutV2Saga() {
  const fallbackUrl = getConfigValue('new_login_url');
  try {
    const response = yield call(Api.logoutV2);
    yield put({ type: constants.LOGOUT_V2_SUCCESS });
    const redirectUri = (response && response.result && response.result.redirectUri) || fallbackUrl;
    window.location.href = redirectUri;
  } catch (error) {
    yield put({ type: constants.LOGOUT_V2_FAILURE, error: error.message });
    window.location.href = fallbackUrl;
  }
}

export function* watchLoginV2() {
  yield takeLatest(constants.LOGIN_V2_REQUEST, loginV2Saga);
}

export function* watchSsoLoginV2() {
  yield takeLatest(constants.SSO_LOGIN_V2_REQUEST, ssoLoginV2Saga);
}

export function* watchCheckSsoDomain() {
  yield takeLatest(constants.CHECK_SSO_DOMAIN_REQUEST, checkSsoDomainSaga);
}

export function* watchValidateOTP() {
  yield takeLatest(constants.VALIDATE_OTP_REQUEST, validateOTPSaga);
}

export function* watchResendOTP() {
  yield takeLatest(constants.RESEND_OTP_REQUEST, resendOTPSaga);
}

export function* watchForgotPassword() {
  yield takeLatest(constants.FORGOT_PASSWORD_REQUEST, forgotPasswordSaga);
}

export function* watchSetPassword() {
  yield takeLatest(constants.SET_PASSWORD_REQUEST, setPasswordSaga);
}

export function* watchCheckOtpSession() {
  yield takeLatest(constants.OTP_SESSION_CHECK_REQUEST, checkOtpSessionSaga);
}

export function* watchLogoutV2() {
  yield takeLatest(constants.LOGOUT_V2_REQUEST, logoutV2Saga);
}

export default function* rootSaga() {
  yield all([
    watchLoginV2(),
    watchSsoLoginV2(),
    watchCheckSsoDomain(),
    watchValidateOTP(),
    watchResendOTP(),
    watchForgotPassword(),
    watchSetPassword(),
    watchCheckOtpSession(),
    watchLogoutV2(),
  ]);
}
